import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/hoa-roofing";
const NAME = "HOA Roofing";
const TITLE = "HOA Roofing Services in Phoenix | One Contractor Valleywide | Durafoam Roofing";
const DESCRIPTION =
  "Board members and property managers: get one contractor, one contract, and consistent pricing across every unit. Durafoam handles HOA roofing valleywide since 1989.";

const COMMUNITIES: Array<{ to: string; label: string }> = [
  { to: "/sincuidados-roofing", label: "Sincuidados" },
  { to: "/troon-roofing", label: "Troon & Troon North" },
  { to: "/desert-highlands-roofing", label: "Desert Highlands" },
  { to: "/desert-mountain-roofing", label: "Desert Mountain" },
  { to: "/silverleaf-roofing", label: "Silverleaf" },
  { to: "/boulders-roofing", label: "The Boulders" },
  { to: "/mccormick-ranch-roofing", label: "McCormick Ranch" },
  { to: "/tatum-ranch-roofing", label: "Tatum Ranch" },
  { to: "/clearwater-hills-roofing", label: "Clearwater Hills" },
  { to: "/firerock-roofing", label: "FireRock" },
  { to: "/vistancia-roofing", label: "Vistancia" },
  { to: "/sun-city-grand-roofing", label: "Sun City Grand" },
];

const faqs = [
  { q: "Do you work with HOAs and property management companies?", a: "Yes. We work with HOA boards and property management firms across the Valley on recurring maintenance, multi-unit re-roofs, phased replacement programs, and warranty service." },
  { q: "Can we get one bid that covers every unit?", a: "Yes. We walk every roof, then price the community as one project with per-unit line items, so the board can approve the whole scope or phase it by building without re-bidding." },
  { q: "Can you support our reserve study?", a: "Yes. We provide condition assessments with age estimates, remaining useful life, and replacement-cost figures that reserve study analysts can use directly." },
  { q: "Do you handle architectural review and permits?", a: "We do. We prepare the submittal package for your architectural committee — system, color, and product data — and pull city or county permits where the scope requires them." },
  { q: "How do you keep residents informed during a multi-building re-roof?", a: "We schedule one building or section at a time and provide advance notices you can distribute, plus a single point of contact residents and the manager can reach." },
  { q: "Can homeowners report leaks directly to you?", a: "Yes, when the HOA authorizes it. We can set up a direct intake number for residents and route invoices through the property manager." },
  { q: "What roof systems do you service for HOAs?", a: "All of them — foam, tile, shingle, flat single-ply, and modified bitumen. Most Phoenix HOAs are mixed; we don't need a single-system community to work with you." },
];

export const Route = createFileRoute("/hoa-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="hoa-roofing"
      eyebrow="For HOA boards & property managers"
      h1="HOA Roofing Services in Phoenix — One Contractor for Your Whole Community"
      subhead="One contractor, one contract, consistent pricing across every unit — from a single leak call to a phased re-roof of the entire community."
      heroBullets={[
        "One bid, every unit",
        "Architectural review & permitting handled",
        "Single point of contact",
        "All roof systems",
      ]}
      ctaLabel="Request an HOA Proposal"
      whatIs={{
        heading: "Built for HOA Boards and Property Managers, Not Just Homeowners",
        body: (
          <>
            <p>
              HOA roofing isn't single-family roofing at a larger scale. A board has to balance
              scope, budget, reserves, resident experience, and warranty tracking across buildings
              of different ages and often different roof systems — and every decision is on the
              record for the next board to inherit.
            </p>
            <p>
              We work the way boards and managers need us to work: one walk of every roof, one
              document trail, itemized pricing per unit or per building, and a schedule you can put
              in front of homeowners before the trucks show up. Condition assessments come back in a
              format your reserve study analyst can use, not a one-page quote.
            </p>
            <p>
              Most of our HOA work is repeat business. Boards rotate; the relationship persists
              because the roofs keep performing and the invoices keep matching the approved scope.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "One Bid, Every Unit — Volume Pricing for Multi-Property Communities",
        body: (
          <>
            <p>
              When a community bids roofs building by building, it pays mobilization, staging, and
              setup costs over and over. Pricing the whole community at once removes that
              duplication, and it locks in one rate so unit 12 isn't quoted differently than unit
              40.
            </p>
            <ul>
              <li><strong>Per-unit line items in one proposal:</strong> the board sees the total and the per-building breakdown, and can approve all of it or phase it without going back out to bid.</li>
              <li><strong>Volume pricing on repeated scope:</strong> identical roofs done back-to-back cost less per roof than the same work spread across three separate mobilizations.</li>
              <li><strong>Phased schedules that protect reserves:</strong> communities built in phases have roofs that age at different rates. Staggering replacement prevents a balloon hit to the reserve fund.</li>
              <li><strong>Foam recoat programs:</strong> communities with foam roofs get scheduled{" "}
                <Link to="/foam-roof-coating-recoating">recoats</Link> on a rotation, which is far cheaper than rebuilding failed foam later. For buildings due for a full system, see{" "}
                <Link to="/commercial-foam-roofing">commercial foam roofing</Link>.</li>
              <li><strong>Tile underlayment cycles:</strong> most Phoenix HOAs with tile face{" "}
                <Link to="/tile-roofing">underlayment replacement</Link> at 20–25 years. Planning it beats emergency-funding it.</li>
              <li><strong>Storm triage:</strong> one monsoon cell can hit every roof in the community. Boards with a pre-engaged roofer get documented and scheduled first — see{" "}
                <Link to="/storm-damage-repair">storm damage repair</Link>.</li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "We Handle HOA Architectural Review & Permitting",
        intro:
          "Approval paperwork is where community roofing projects stall. We prepare it, submit it, and track it so the board isn't chasing documents.",
        steps: [
          {
            title: "Community assessment",
            body: "We walk every roof and document conditions, age, remaining life, and replacement projections in a format suitable for reserve study use.",
          },
          {
            title: "Architectural submittal package",
            body: "System, product data, color and profile selections, and specification sheets prepared for your architectural committee or design review board.",
          },
          {
            title: "Permits and compliance",
            body: "We pull city or county permits where the scope requires them, coordinate inspections, and keep documentation the association can file.",
          },
          {
            title: "Service agreement and resident communication",
            body: "Scope, response times, billing flow, and notice templates are set before work starts. Direct resident intake if the board wants it.",
          },
          {
            title: "Phased work, reporting, and warranty tracking",
            body: "One building or section at a time, itemized reports per visit and per project, and warranties documented so coverage stays valid through board turnover.",
          },
        ],
      }}
      whyChoose={{
        heading: "Communities We Already Work In",
        body: (
          <>
            <p>
              We've roofed and maintained homes across many of the Valley's established and
              guard-gated communities. If your community is on this list, we already know the
              architectural standards, the access constraints, and the roof systems in place:
            </p>
            <ul>
              {COMMUNITIES.map((c) => (
                <li key={c.to}>
                  <Link to={c.to}>{c.label}</Link>
                </li>
              ))}
            </ul>
            <p>
              Not listed? We work valleywide — see every{" "}
              <Link to="/service-areas">community and city we serve</Link>. Multi-building and
              mixed-use properties are also covered under{" "}
              <Link to="/apartment-roofing">apartment & condo roofing</Link> and{" "}
              <Link to="/commercial-roofing">commercial roofing</Link>.
            </p>
          </>
        ),
      }}
      extraSection={{
        heading: "A Single Point of Contact for the Whole Project",
        body: (
          <>
            <p>
              One person owns your community's account: scheduling, resident questions, change
              requests, invoices, and warranty claims. No routing a leak call through a call center
              and hoping someone follows up.
            </p>
            <ul>
              <li>One contact for the board and the property manager, from proposal through final walkthrough</li>
              <li>Advance building-by-building notices residents actually receive before work starts</li>
              <li>Optional direct resident intake line, with billing still routed through the manager</li>
              <li>Itemized reporting after every visit so meeting minutes write themselves</li>
              <li>Warranty documentation stored on our side too, so a new board isn't starting from zero</li>
            </ul>
            <p>
              Budget planning question? Our{" "}
              <Link to="/commercial-roofing-cost-phoenix">Phoenix commercial and multi-unit cost guide</Link>{" "}
              covers the ranges boards ask about most, and we can discuss{" "}
              <Link to="/financing">payment and phasing options</Link> for larger community
              projects.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Request an HOA Roofing Proposal"
      ctaBody="Tell us about your community and we'll walk the roofs, meet with your board or manager, and put one proposal in front of you covering every unit."
      formProps={{
        eyebrow: "For boards & property managers",
        heading: "Request an HOA roofing proposal",
        subhead:
          "I'm contacting on behalf of an HOA or community. Tell us the community, your role, and roughly how many units — we'll take it from there.",
        messagePlaceholder: "Number of units, roof types, and what you need (optional)",
        submitLabel: "Request HOA proposal",
        source: "hoa-proposal",
        serviceTag: "HOA Roofing Proposal",
        hoaFields: true,
      }}
    />
  );
}
